import { DateTimePicker } from '@mui/x-date-pickers'
import React from 'react'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'

/**
 * Story 80 — the day-and-hour control of an external job's return timing, in DATE mode.
 *
 * Extracted into its own component for a concrete reason, not for tidiness: {@code useDateFormatter} reads
 * {@code dateFormats} off the MUI theme ({@code dateFormats[i18n.language]}), so without a themed provider it
 * dereferences undefined and the render throws. Calling it at the top of NodeGeneralProperties made the whole
 * job panel depend on that provider and broke every existing test of the panel, including for jobs that are
 * not external and never show a date at all.
 *
 * Mounting it only in DATE mode keeps the dependency where it is actually needed: a non-external job, and an
 * external one in DURATION mode, never touch the hook.
 *
 * The text field is customised through `slotProps`, the API of the installed `@mui/x-date-pickers` v7. Not
 * `renderInput`: that is the v5 API, it was removed in v6 and v7 ignores it in silence, so `size` and the
 * shrunk label were simply never applied. `planner/PinJobEventDialog.jsx` still carries that legacy.
 *
 * @param {Object} props
 * @param {Date|string|null} props.value - Stored return date
 * @param {boolean} props.disabled
 * @param {function(Date)} props.onChange
 */
const ReturnDateField = ({ value, disabled, onChange }) => {
  const { t_ } = useSmartTranslation()
  const { format, ampm, parseDateTime, formatDateTime } = useDateFormatter()

  return (
    <DateTimePicker
      ampm={ampm}
      label={t_('Return date')}
      format={format.dateTime}
      value={parseDateTime(formatDateTime(value))}
      disabled={disabled}
      onChange={onChange}
      slotProps={{
        textField: {
          size: 'small',
          // Bounded on purpose. The picker's default input is the widest control of the panel's top
          // row; left unbounded it overflowed the line and pushed the mode selector and this field
          // into each other. Every sibling of that row bounds its own width the same way.
          sx: { width: 200 },
          InputLabelProps: { shrink: true }
        }
      }}
    />
  )
}

export default ReturnDateField
